import React from "react";
import {
  resolveSocialLinkAlt,
  resolveSocialLinkIconUrl,
} from '../../domain/media';
import type { SocialBlock } from '../../domain/types';
import { EditorImageSkeleton } from '../../canvas/EditorImageSkeleton';
import { blockAlignShell } from '../block-align-shell';
import { padStyle, presentationTableProps } from '../table';
import {
  EDITOR_IMG_PROPS,
  linkPropsForMode,
} from './editor-props';
import { merge } from './merge';
import type { BlockRenderProps } from './types';

export function renderSocialBlock({
  block,
  facility,
  mergeMode,
  editorMode = false,
  isEditorIconBytesLoaded,
  onEditorIconBytesLoaded,
}: BlockRenderProps & { block: SocialBlock }) {
  const iconsTable = (
    <table
      {...presentationTableProps()}
      style={{ display: 'inline-table', borderCollapse: 'collapse' }}>
      <tbody>
        <tr>
          <td style={{ lineHeight: 0 }}>
            {block.links.map((link, index) => {
              const resolvedHref = merge(link.url.trim(), mergeMode) || '';
              const linkProps =
                editorMode && resolvedHref.length > 0
                  ? linkPropsForMode(true, resolvedHref)
                  : linkPropsForMode(false, resolvedHref);
              const spacing =
                index < block.links.length - 1 ? block.gap : 0;
              const iconSrc = resolveSocialLinkIconUrl(
                link,
                block.iconVariant,
                facility,
              );
              const iconSizePx = `${block.iconSize}px`;
              const bytesPending =
                editorMode &&
                (iconSrc.length === 0 ||
                  isEditorIconBytesLoaded?.(link.id, iconSrc) !== true);
              const icon = (
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: block.iconSize,
                    height: block.iconSize,
                    lineHeight: 0,
                  }}>
                  {bytesPending ? (
                    <EditorImageSkeleton
                      width={iconSizePx}
                      height={iconSizePx}
                    />
                  ) : null}
                  {iconSrc.length > 0 ? (
                    <img
                      src={iconSrc}
                      alt={resolveSocialLinkAlt(link)}
                      width={block.iconSize}
                      height={block.iconSize}
                      {...(editorMode ? EDITOR_IMG_PROPS : {})}
                      onLoad={
                        editorMode
                          ? () =>
                              onEditorIconBytesLoaded?.(link.id, iconSrc)
                          : undefined
                      }
                      onError={
                        editorMode
                          ? () =>
                              onEditorIconBytesLoaded?.(link.id, iconSrc)
                          : undefined
                      }
                      style={{
                        position: bytesPending ? 'absolute' : undefined,
                        inset: bytesPending ? 0 : undefined,
                        width: block.iconSize,
                        height: block.iconSize,
                        display: 'block',
                        border: 0,
                        opacity: bytesPending ? 0 : 1,
                      }}
                    />
                  ) : null}
                </span>
              );
              const shellStyle = {
                display: 'inline-block' as const,
                marginRight: spacing,
                lineHeight: 0,
              };

              if (linkProps) {
                return (
                  <a key={link.id} {...linkProps} style={shellStyle}>
                    {icon}
                  </a>
                );
              }

              return (
                <span key={link.id} style={shellStyle}>
                  {icon}
                </span>
              );
            })}
          </td>
        </tr>
      </tbody>
    </table>
  );

  return blockAlignShell(block.align, iconsTable, {
    ...padStyle(block.padding),
    lineHeight: 0,
  });
}
